import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import type { AnalysisRunFilesQuery } from '@/graphql/graphql';
import { analysisRunFilesQuery } from '@/queries/analysisRuns.query';
import { batchByproductsQuery } from '@/queries/byproduct.query';

import { ANALYSIS_RUN_FILES_KEY } from './runFilesKey';
import { finishedRunsSignature, groupRunFiles, type AnalysisRunFileItem } from './runList';
import { ANALYSIS_RUN_JOB_TYPES, type AnalysisRunItem } from './runs';
import { navigateToDownload } from './navigateToDownload';

export const analysisRunFilesQueryKey = (projectId: string, signature: string) =>
  [ANALYSIS_RUN_FILES_KEY, projectId, signature] as const;

/** Each run completion makes a new key; an inactive one is garbage the moment
 *  the next replaces it, so don't keep it for the app-wide 7-day `gcTime`. */
export const ANALYSIS_RUN_FILES_GC_MS = 5 * 60_000;

/**
 * The downloadable files of the project's runs, grouped per run id.
 *
 * NEVER polled: the server scans report jobs' `inputJson` for this. It
 * refetches when the finished-runs signature in its key changes (a run
 * finished) and when `useReportProgress` invalidates it (a standalone report
 * completed). Waits for `runs` so the first fetch already carries the
 * signature; `keepPreviousData` keeps the buttons in place across a key change.
 */
export function useAnalysisRunFiles(projectId: string | null | undefined, runs: readonly AnalysisRunItem[] | undefined) {
  const signature = finishedRunsSignature(runs);
  return useQuery({
    queryKey: analysisRunFilesQueryKey(projectId ?? '', signature),
    // The same `types` as `useAnalysisRuns`: the server's newest-200 window
    // must hold the same runs as the list, or files go missing past 200.
    queryFn: () => execute(analysisRunFilesQuery, { projectId: projectId!, types: [...ANALYSIS_RUN_JOB_TYPES] }),
    enabled: !!projectId && runs !== undefined,
    select: selectRunFiles,
    placeholderData: keepPreviousData,
    // Mount / window focus refetch at most once a minute — not a poll.
    staleTime: 60_000,
    gcTime: ANALYSIS_RUN_FILES_GC_MS,
  });
}

// Keep the selector identity stable across drawer URL updates. Otherwise
// TanStack Query rebuilds every RunFiles value on each list render, defeating
// memoized rows even when the files have not changed.
function selectRunFiles(data: AnalysisRunFilesQuery) {
  return groupRunFiles(data.analysisRunFiles ?? []);
}

/**
 * Click-time download: URLs are presigned for 5 minutes, so the list carries
 * none. Resolve it through the file's batch, then hand it to a hidden iframe
 * (`navigateToDownload`): the presigned URL carries `Content-Disposition:
 * attachment`, so the browser downloads and the page never navigates — and if
 * the object is already gone, S3's error page lands in the invisible frame,
 * not over the app.
 * A loading toast covers the URL lookup, then switches to success once the
 * browser starts the download (the iframe cannot report transfer completion).
 *
 * The list can be a minute stale, so the file (or its whole batch: `batch(id)`
 * throws "Batch not found") may have been deleted since. Every failure of the
 * lookup is that same user-facing case: one translated "no longer available"
 * toast, never a raw server / GraphQL message.
 */
export function useDownloadRunFile(): (file: Pick<AnalysisRunFileItem, 'id' | 'batchId'>) => Promise<void> {
  const { t } = useTranslation('project');
  return async (file) => {
    const toastId = toast.loading(t('analyses.files.preparingDownload'));
    let url: string | null | undefined;
    try {
      const data = await execute(batchByproductsQuery, { id: file.batchId });
      url = (data.batch?.byproducts ?? []).find((b) => b.id === file.id)?.downloadUrl;
    } catch (err) {
      console.warn('analysis run file lookup failed', err);
    }
    if (url) {
      navigateToDownload(url);
      toast.success(t('analyses.files.downloadStarted'), { id: toastId });
    } else toast.error(t('analyses.files.notFound'), { id: toastId });
  };
}
